// frontend/src/contexts/ThemeContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { createAppTheme } from '../theme';

const ThemeContext = createContext();

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

export const ThemeContextProvider = ({ children }) => {
  // Match react-snap's initial light markup during hydration. Reading
  // localStorage here can otherwise switch the theme before React hydrates.
  const [darkMode, setDarkMode] = useState(false);
  const [hasLoadedTheme, setHasLoadedTheme] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('darkMode');
      if (saved) setDarkMode(JSON.parse(saved));
    } catch (error) {
      console.warn('Failed to restore theme preference:', error);
    } finally {
      setHasLoadedTheme(true);
    }
  }, []);

  useEffect(() => {
    if (hasLoadedTheme) {
      try {
        localStorage.setItem('darkMode', JSON.stringify(darkMode));
      } catch (error) {
        console.warn('Failed to save theme preference:', error);
      }
    }
  }, [darkMode, hasLoadedTheme]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const theme = createAppTheme(darkMode);

  const value = {
    darkMode,
    toggleDarkMode,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
